import { createTheme } from '@mui/material/styles';

/**
 * ckMuiTheme — the MUI theme of the admin screens under the new design.
 *
 * The screens are still MUI; this theme dresses them in the crm-kit/next tokens so they look like
 * the rest of the new design (same surfaces, borders, radii, type, light / dark, modern / classic / retro).
 * Palette values mirror tokens.css (MUI needs real colours for its alpha math); surfaces and borders
 * read the live CSS variables. Every modal / popper renders into the CkThemeProvider portal so the
 * variables resolve there too (see `container`).
 */
const SETS = {
  'modern-light': {
    bg: '#f5f6fa', surface: '#ffffff', surface2: '#f9fafb', surface3: '#f3f4f6', border: '#e5e7eb', borderStrong: '#d1d5db', hover: '#fafaff',
    text: '#111827', text2: '#4b5563', text3: '#9ca3af', onPrimary: '#ffffff',
    primary: '#4f46e5', primaryHover: '#4338ca', primarySoft: '#eef2ff',
    success: '#059669', warning: '#b45309', danger: '#dc2626', info: '#0284c7', violet: '#7c3aed',
    radiusSm: 6, radius: 10, radiusLg: 14,
    font: '"Assistant", "Heebo", Arial, sans-serif', fontDisplay: '"Assistant", "Heebo", Arial, sans-serif',
  },
  'modern-dark': {
    bg: '#0f1117', surface: '#171a22', surface2: '#1d212b', surface3: '#232835', border: '#2a2f3a', borderStrong: '#3a404d', hover: '#1c2030',
    text: '#e7e9ee', text2: '#a9afbc', text3: '#737b8a', onPrimary: '#0f1117',
    primary: '#8b93ff', primaryHover: '#a5b4fc', primarySoft: 'rgba(139, 147, 255, 0.15)',
    success: '#34d399', warning: '#fbbf24', danger: '#f87171', info: '#38bdf8', violet: '#c4b5fd',
    radiusSm: 6, radius: 10, radiusLg: 14,
    font: '"Assistant", "Heebo", Arial, sans-serif', fontDisplay: '"Assistant", "Heebo", Arial, sans-serif',
  },
  'classic-light': {
    bg: '#f7f6f2', surface: '#ffffff', surface2: '#f7f6f2', surface3: '#f0eee8', border: '#e3e0d8', borderStrong: '#c9c5ba', hover: '#faf9f6',
    text: '#1a1a1a', text2: '#55524b', text3: '#8b877d', onPrimary: '#ffffff',
    primary: '#1f5c45', primaryHover: '#174634', primarySoft: '#e8f0eb',
    success: '#1f5c45', warning: '#8a5300', danger: '#a3261b', info: '#1d4f73', violet: '#7c3aed',
    radiusSm: 2, radius: 3, radiusLg: 4,
    font: '"IBM Plex Sans Hebrew", "Assistant", Arial, sans-serif', fontDisplay: '"Frank Ruhl Libre", "David", serif',
  },
  'retro-light': {
    bg: '#efe6d2', surface: '#fbf6ea', surface2: '#f3ead6', surface3: '#e6dcc6', border: '#1d1a16', borderStrong: '#1d1a16', hover: '#f7efd9',
    text: '#1d1a16', text2: '#4a4137', text3: '#8a7d6b', onPrimary: '#fff8ec',
    primary: '#c4552d', primaryHover: '#a8431f', primarySoft: '#f6dcc8',
    success: '#2f7f5f', warning: '#9a6a0c', danger: '#b3261e', info: '#2f6f7f', violet: '#6d3f7a',
    radiusSm: 3, radius: 4, radiusLg: 6,
    font: '"Rubik", "Assistant", Arial, sans-serif', fontDisplay: '"Secular One", "Rubik", sans-serif',
  },
};

export function ckTokenSet(style, resolvedMode) {
  if (style === 'modern' || !style) return SETS[resolvedMode === 'dark' ? 'modern-dark' : 'modern-light'];
  return SETS[`${style}-light`] || SETS['modern-light'];
}

const v = (name, fallback) => `var(--ck-${name}, ${fallback})`;

/**
 * @param {string} style        modern | classic | retro
 * @param {string} resolvedMode light | dark
 * @param {() => Element} getContainer  where modals / poppers mount (the ck portal)
 */
export default function createCkMuiTheme(style, resolvedMode, getContainer) {
  const t = ckTokenSet(style, resolvedMode);
  const dark = resolvedMode === 'dark' && (style === 'modern' || !style);
  const container = getContainer ? () => getContainer() || document.body : undefined;
  const shadow1 = v('shadow-1', '0 1px 2px rgba(16,24,40,.06)');
  const shadow2 = v('shadow-2', '0 4px 12px rgba(16,24,40,.08)');
  const shadow3 = v('shadow-3', '0 20px 48px rgba(16,24,40,.18)');
  const shadows = ['none', shadow1, shadow1, shadow1, shadow2, shadow2, shadow2, shadow2, shadow3];
  while (shadows.length < 25) shadows.push(shadow3);

  return createTheme({
    direction: 'rtl',
    palette: {
      mode: dark ? 'dark' : 'light',
      primary: { main: t.primary, dark: t.primaryHover, contrastText: t.onPrimary },
      secondary: { main: t.violet, contrastText: '#ffffff' },
      success: { main: t.success },
      warning: { main: t.warning },
      error: { main: t.danger },
      info: { main: t.info },
      background: { default: t.bg, paper: t.surface },
      text: { primary: t.text, secondary: t.text2, disabled: t.text3 },
      divider: t.border,
      action: { hover: t.hover, selected: t.primarySoft },
      ...(dark ? { grey: { 50: '#1d212b', 100: '#232835', 200: '#2a2f3a', 300: '#3a404d' } } : {}),
    },
    shape: { borderRadius: t.radius },
    shadows,
    typography: {
      fontFamily: t.font,
      h1: { fontFamily: t.fontDisplay, fontWeight: 800 },
      h2: { fontFamily: t.fontDisplay, fontWeight: 800 },
      h3: { fontFamily: t.fontDisplay, fontWeight: 800 },
      h4: { fontFamily: t.fontDisplay, fontWeight: 800, fontSize: '1.6rem' },
      h5: { fontFamily: t.fontDisplay, fontWeight: 800, fontSize: '1.35rem' },
      h6: { fontFamily: t.fontDisplay, fontWeight: 700, fontSize: '1.08rem' },
      subtitle1: { fontWeight: 600 },
      subtitle2: { fontWeight: 700 },
      button: { textTransform: 'none', fontWeight: 700 },
    },
    components: {
      MuiModal: { defaultProps: { container } },
      MuiPopper: { defaultProps: { container } },
      MuiPaper: {
        styleOverrides: {
          root: { backgroundImage: 'none', backgroundColor: v('surface', t.surface), color: v('text', t.text) },
          rounded: { borderRadius: v('radius-lg', `${t.radiusLg}px`) },
          outlined: { borderColor: v('border', t.border), borderWidth: v('border-w', '1px') },
          elevation1: { boxShadow: shadow1, border: `${v('border-w', '1px')} solid ${v('border', t.border)}` },
          elevation2: { boxShadow: shadow1, border: `${v('border-w', '1px')} solid ${v('border', t.border)}` },
          elevation3: { boxShadow: shadow2, border: `${v('border-w', '1px')} solid ${v('border', t.border)}` },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: { borderRadius: v('radius-lg', `${t.radiusLg}px`), border: `${v('border-w', '1px')} solid ${v('border', t.border)}`, boxShadow: shadow1 },
        },
      },
      MuiButton: {
        defaultProps: { disableElevation: true },
        styleOverrides: {
          root: { borderRadius: v('radius-sm', `${t.radiusSm}px`), fontWeight: 700, minHeight: 36, paddingInline: 14, whiteSpace: 'nowrap' },
          sizeSmall: { minHeight: 30, paddingInline: 10, fontSize: 13 },
          outlined: { borderColor: v('border-strong', t.borderStrong), borderWidth: v('border-w', '1px'), backgroundColor: v('surface', t.surface) },
          outlinedPrimary: { color: v('text', t.text), '&:hover': { borderColor: v('primary', t.primary), backgroundColor: v('primary-soft', t.primarySoft) } },
          containedPrimary: { '&:hover': { backgroundColor: v('primary-hover', t.primaryHover) } },
          text: { '&:hover': { backgroundColor: v('surface-2', t.surface2) } },
        },
      },
      MuiIconButton: {
        styleOverrides: { root: { borderRadius: v('radius-sm', `${t.radiusSm}px`), '&:hover': { backgroundColor: v('surface-3', t.surface3) } } },
      },
      MuiButtonGroup: { defaultProps: { disableElevation: true } },
      MuiToggleButton: {
        styleOverrides: {
          root: {
            textTransform: 'none', fontWeight: 600, borderColor: v('border-strong', t.borderStrong), color: v('text-2', t.text2),
            '&.Mui-selected': { backgroundColor: v('primary-soft', t.primarySoft), color: v('primary', t.primary) },
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: v('radius-sm', `${t.radiusSm}px`),
            backgroundColor: v('surface', t.surface),
            '& .MuiOutlinedInput-notchedOutline': { borderColor: v('border-strong', t.borderStrong), borderWidth: v('border-w', '1px') },
            '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: v('text-3', t.text3) },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: v('primary', t.primary), borderWidth: v('border-w', '1px') },
            '&.Mui-focused': { boxShadow: `0 0 0 3px ${v('primary-ring', 'rgba(79,70,229,.25)')}` },
            '&.Mui-error .MuiOutlinedInput-notchedOutline': { borderColor: v('danger', t.danger) },
          },
          input: { fontWeight: 500 },
        },
      },
      MuiInputLabel: { styleOverrides: { root: { color: v('text-2', t.text2), fontWeight: 600 } } },
      MuiFormLabel: { styleOverrides: { root: { color: v('text-2', t.text2) } } },
      MuiTableContainer: { styleOverrides: { root: { borderRadius: v('radius', `${t.radius}px`) } } },
      MuiTableHead: {
        styleOverrides: {
          root: {
            '& .MuiTableCell-head': {
              backgroundColor: v('th-bg', t.surface2), color: v('th-text', t.text2), fontWeight: 700, fontSize: 12.5,
              borderBottom: `${v('border-w', '1px')} solid ${v('border', t.border)}`, whiteSpace: 'nowrap',
            },
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: { borderBottom: `1px solid ${v('row-line', t.border)}`, color: v('text', t.text) },
          stickyHeader: { backgroundColor: v('th-bg', t.surface2) },
        },
      },
      MuiTableRow: {
        styleOverrides: {
          root: {
            '&.MuiTableRow-hover:hover': { backgroundColor: v('row-hover', t.hover) },
            '&.Mui-selected, &.Mui-selected:hover': { backgroundColor: v('row-selected', t.primarySoft) },
          },
        },
      },
      MuiTablePagination: { styleOverrides: { root: { color: v('text-2', t.text2) } } },
      MuiDialog: {
        styleOverrides: {
          paper: {
            borderRadius: v('radius-lg', `${t.radiusLg}px`), boxShadow: shadow3,
            border: `${v('border-w', '1px')} solid ${v('border', t.border)}`, backgroundColor: v('surface', t.surface),
          },
        },
      },
      MuiBackdrop: { styleOverrides: { root: { '&:not(.MuiBackdrop-invisible)': { backgroundColor: v('scrim', 'rgba(17,24,39,.35)') } } } },
      MuiDialogTitle: { styleOverrides: { root: { fontWeight: 800, fontSize: 18, fontFamily: t.fontDisplay } } },
      MuiDialogActions: {
        styleOverrides: { root: { padding: '12px 20px', gap: 8, borderTop: `1px solid ${v('border', t.border)}`, backgroundColor: v('surface-2', t.surface2) } },
      },
      MuiMenu: {
        styleOverrides: {
          paper: { borderRadius: v('radius', `${t.radius}px`), border: `${v('border-w', '1px')} solid ${v('border', t.border)}`, boxShadow: shadow3 },
        },
      },
      MuiPopover: {
        styleOverrides: {
          paper: { borderRadius: v('radius', `${t.radius}px`), border: `${v('border-w', '1px')} solid ${v('border', t.border)}`, boxShadow: shadow3 },
        },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            fontSize: 14, borderRadius: v('radius-sm', `${t.radiusSm}px`), marginInline: 4,
            '&.Mui-selected': { backgroundColor: v('primary-soft', t.primarySoft) },
          },
        },
      },
      MuiListItemButton: {
        styleOverrides: {
          root: {
            borderRadius: v('radius-sm', `${t.radiusSm}px`),
            '&.Mui-selected': { backgroundColor: v('primary-soft', t.primarySoft), color: v('primary', t.primary) },
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: { fontWeight: 600, borderRadius: v('radius-pill', '999px') },
          outlined: { borderColor: v('border-strong', t.borderStrong) },
        },
      },
      MuiTabs: {
        styleOverrides: {
          root: { minHeight: 42, borderBottom: `1px solid ${v('border', t.border)}` },
          indicator: { height: 2, borderRadius: 2, backgroundColor: v('accent', t.primary) },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            textTransform: 'none', fontWeight: 600, minHeight: 42, fontSize: 14, color: v('text-2', t.text2),
            '&.Mui-selected': { color: v('text', t.text), fontWeight: 700 },
          },
        },
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: { backgroundColor: v('inverse', '#111827'), color: v('on-inverse', '#fff'), fontSize: 12, borderRadius: v('radius-sm', `${t.radiusSm}px`), boxShadow: shadow2 },
          arrow: { color: v('inverse', '#111827') },
        },
      },
      MuiAlert: {
        styleOverrides: {
          root: { borderRadius: v('radius', `${t.radius}px`), alignItems: 'center' },
          standardSuccess: { backgroundColor: v('success-soft', '#ecfdf5'), color: v('text', t.text), '& .MuiAlert-icon': { color: v('success', t.success) } },
          standardInfo: { backgroundColor: v('info-soft', '#f0f9ff'), color: v('text', t.text), '& .MuiAlert-icon': { color: v('info', t.info) } },
          standardWarning: { backgroundColor: v('warning-soft', '#fffbeb'), color: v('text', t.text), '& .MuiAlert-icon': { color: v('warning', t.warning) } },
          standardError: { backgroundColor: v('danger-soft', '#fef2f2'), color: v('text', t.text), '& .MuiAlert-icon': { color: v('danger', t.danger) } },
        },
      },
      MuiAccordion: {
        defaultProps: { disableGutters: true, elevation: 0 },
        styleOverrides: {
          root: {
            border: `${v('border-w', '1px')} solid ${v('border', t.border)}`, borderRadius: v('radius', `${t.radius}px`),
            '&:before': { display: 'none' }, '&:not(:last-of-type)': { marginBottom: 8 },
          },
        },
      },
      MuiDivider: { styleOverrides: { root: { borderColor: v('border', t.border) } } },
      MuiDrawer: { styleOverrides: { paper: { backgroundColor: v('bg', t.bg), borderColor: v('border', t.border) } } },
      MuiLinearProgress: { styleOverrides: { root: { borderRadius: 999, backgroundColor: v('surface-3', t.surface3) } } },
      MuiSnackbarContent: { styleOverrides: { root: { borderRadius: v('radius', `${t.radius}px`), backgroundColor: v('inverse', '#111827'), color: v('on-inverse', '#fff') } } },
      MuiAvatar: { styleOverrides: { colorDefault: { backgroundColor: v('primary-soft', t.primarySoft), color: v('primary', t.primary), fontWeight: 700 } } },
    },
  });
}
